import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {
    ActivityProjection,
    IAction,
    IActivity,
    IActivityCreate,
    IActivityCreated,
    IActivitySurveyCreate,
    IActivitySurveyCreated,
    IBaseId,
    IDocument,
    IEntitySelectTable,
    INote,
    IPagedData,
    IPagedSearch,
    IParticipantSelect,
    IPayment,
    IPendingActionSearch,
    IReferral,
    IUser,
    IUserTable,
} from '@evencel/models';
import {Observable, take, tap} from 'rxjs';

export const ACTIVITY_URL = 'activities';

export type ActivtyDataTypes =
    | 'conflictResolution'
    | 'training'
    | 'restorativePractices'
    | 'restorativePracticesTraining'
    | 'courtCases'
    | 'fundraising'
    | 'grants'
    | 'organizationalNetworking'
    | 'deliberativeDialogue'
    | 'pmp'
    | 'courtCase'
    | 'reEntry'
    | 'tenancy'
    | 'reEntryOutreach'
    | 'outreach'
    | 'divorceAndFamilyPmp'
    | 'advancedTraining';

@Injectable({providedIn: 'root'})
export class ActivityService {
    private httpClient = inject(HttpClient);

    // Activity Data
    addActivityData(_id: string, data: unknown, path: ActivtyDataTypes) {
        let params = new HttpParams();
        params = params.append('projection', ActivityProjection.activityData);
        const url = `${ACTIVITY_URL}/${_id}/activity-data/${path}`;
        return this.httpClient.patch(url, data, {params});
    }

    removeActivityData(_id: string, path: string, id?: string) {
        let params = new HttpParams();
        params = params.append('projection', ActivityProjection.activityData);
        const url = `${ACTIVITY_URL}/${_id}/activity-data/${path}/${id}`;
        return this.httpClient.delete(url, {params});
    }

    updateActivityData(_id: string, data: IBaseId, path: string) {
        let params = new HttpParams();
        params = params.append('projection', ActivityProjection.activityData);
        const url = `${ACTIVITY_URL}/${_id}/activity-data/${path}/${data._id}`;
        return this.httpClient.patch(url, data, {params});
    }

    getPmpCaseNumber(pmpCaseNumber: string) {
        const url = `${ACTIVITY_URL}/activity-data/divorce-and-family/pmp-case-number/${pmpCaseNumber}`;
        return this.httpClient.get<IActivityCreated>(url).pipe(take(1));
    }

    // Entity
    updateEntityData(_id: string, payload: IReferral | IEntitySelectTable[], path: string) {
        const url = `${ACTIVITY_URL}/${_id}/entity-data/${path}`;
        return this.httpClient.patch(url, payload).pipe(take(1));
    }

    // User
    addUser(_id: string, user: IUser, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/users`;
        return this.httpClient.patch(url, user, {params});
    }

    addUserParticipant(_id: string, user: IUser, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/user-participants`;
        return this.httpClient.post(url, user, {params});
    }

    removeUserFromActivity(_id: string, user: IUser, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/users/${user._id}`;
        return this.httpClient.delete(url, {params});
    }

    removeUserParticipant(_id: string, user: IUserTable, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/user-participants/${user._id}`;
        return this.httpClient.delete(url, {params});
    }

    // Actions
    addAction(_id: string, action: IAction, projection: string = ActivityProjection.action) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/action`;
        return this.httpClient.post(url, action, {params});
    }

    getAction(_id: string, action: IAction) {
        const url = `${ACTIVITY_URL}/${_id}/action/${action._id}`;
        return this.httpClient.get<IAction>(url);
    }

    updateAction(_id: string, action: IAction) {
        const url = `${ACTIVITY_URL}/${_id}/action/${action._id}`;
        return this.httpClient.patch(url, action);
    }

    removeAction(_id: string, action: IAction) {
        const url = `${ACTIVITY_URL}/${_id}/action/${action._id}`;
        return this.httpClient.delete(url);
    }

    reverseAction(_id: string, participant: IParticipantSelect) {
        const url = `${ACTIVITY_URL}/${_id}/action-reverse/${participant._id}`;
        return this.httpClient.delete(url).pipe(take(1));
    }

    // Docuemnt
    addDoc(_id: string, document: IDocument, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/docs`;
        return this.httpClient.post<IDocument>(url, document, {params});
    }

    deleteDoc(_id: string, doc: IDocument) {
        const url = `${ACTIVITY_URL}/${_id}/docs/${doc._id}`;
        return this.httpClient.delete(url);
    }

    downloadDoc(_id: string, doc: IDocument) {
        const url = `${ACTIVITY_URL}/${_id}/docs/${doc._id}/download`;
        return this.httpClient.post(url, {}, {responseType: 'arraybuffer'}).pipe(
            tap(data => {
                const blob: Blob = new Blob([data]);
                const objectUrl: string = URL.createObjectURL(blob);
                const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
                a.href = objectUrl;
                a.download = doc.name;
                document.body.appendChild(a);
                a.click();

                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
            })
        );
    }

    uploadDoc(_id: string, formData: FormData) {
        const url = `${ACTIVITY_URL}/${_id}/docs/upload`;
        return this.httpClient.post<{Key: string}>(url, formData);
    }

    // Note
    addNote(_id: string, note: INote, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/notes`;
        return this.httpClient.post(url, note, {params});
    }

    updateNote(_id: string, note: INote, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/notes/${note._id}`;
        return this.httpClient.patch(url, note, {params});
    }

    removeNote(_id: string, note: INote, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/notes/${note._id}`;
        return this.httpClient.delete(url, {params});
    }

    //Payment
    addPayment(_id: string, payments: IPayment, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/payments`;
        return this.httpClient.patch(url, payments, {params});
    }

    updatePayment(_id: string, payments: IPayment) {
        const url = `${ACTIVITY_URL}/${_id}/payments/${payments._id}`;
        return this.httpClient.patch(url, payments);
    }

    removePayment(_id: string, payments: IPayment, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/payments/${payments._id}`;
        return this.httpClient.delete(url, {params});
    }

    // Survey
    addSurvey(_id: string, surveys: IActivitySurveyCreate[], projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/survey`;
        return this.httpClient.post(url, surveys, {params});
    }

    updateSurvey(_id: string, survey: Partial<IActivitySurveyCreated>, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/survey/${survey._id}`;
        return this.httpClient.patch(url, survey, {params});
    }

    removeSurvey(_id: string, survey: IActivitySurveyCreated, projection: string) {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${_id}/survey/${survey._id}`;
        return this.httpClient.delete(url, {params});
    }

    // Dashboard
    searchDashboard(data: IPagedSearch, filter: IPendingActionSearch): Observable<IPagedData<IActivity>> {
        const params = this.searchParams(data);
        const url = `${ACTIVITY_URL}/dashboard/pending`;
        return this.httpClient.post<IPagedData<IActivity>>(url, filter, {params});
    }

    activityStatusDashboard(filter: IPendingActionSearch): Observable<IPagedData<IActivity>> {
        const url = `${ACTIVITY_URL}/dashboard/status`;
        return this.httpClient.post<IPagedData<IActivity>>(url, filter);
    }

    post(data: IActivityCreate): Observable<IActivity> {
        return this.httpClient.post<IActivity>(ACTIVITY_URL, data);
    }

    patch(data: IActivity, projection: string): Observable<IActivityCreated> {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${data._id}`;
        return this.httpClient.patch<IActivityCreated>(url, data, {params}).pipe(take(1));
    }

    delete(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${ACTIVITY_URL}/${id}`);
    }

    restore(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${ACTIVITY_URL}/${id}/restore`);
    }

    private searchParams(data: IPagedSearch) {
        const {active, direction} = data.sort;
        const {pageIndex, pageSize} = data.page;
        let params = new HttpParams();
        params = params.append('pageIndex', `${pageIndex}`);
        params = params.append('pageSize', `${pageSize}`);
        params = params.append('sort', active);
        params = params.append('order', direction);
        params = params.append('projection', data.projection || 'dataTable');
        return params;
    }

    search(data: IPagedSearch): Observable<IPagedData<IActivity>> {
        const params = this.searchParams(data);
        return this.httpClient.post<IPagedData<IActivity>>(`${ACTIVITY_URL}/search`, data.condition, {params});
    }

    getOne(id: string, projection = 'general'): Observable<IActivityCreated> {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${ACTIVITY_URL}/${id}`;
        return this.httpClient.get<IActivityCreated>(url, {params}).pipe(take(1));
    }

    get(data: IPagedSearch): Observable<IPagedData<IActivity>> {
        const params = this.searchParams(data);
        return this.httpClient.get<IPagedData<IActivity>>(ACTIVITY_URL, {params});
    }
}
